import { LoadCurveExt } from '@/classes/scenario/load/LoadCurveExt';
import { LoadFileExt } from '@/classes/scenario/load/LoadFileExt';
import { ScenarioLoadExt } from '@/classes/scenario/load/ScenarioLoadExt';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { FieldStatus } from '@/classes/FieldStatus';
import { LoadCurve } from '@/dto/scenario/load/LoadCurve';
import { ScenarioLoad } from '@/dto/scenario/load/ScenarioLoad';
import { ScenarioPayloads } from '@/store/modules/scenario';

interface RuleHelper {
  getDtoFieldStatuses: (scenario: ScenarioExt, path: string[]) => FieldStatus[]
  cleanScenario: (scenario: ScenarioExt, fieldStatuses: FieldStatus[]) => void
}

interface Dependencies {
  post: <T>(url: string, body: unknown, cancellationKey: string) => Promise<{data: T}>
  errorHandler: (error: unknown) => void
  ruleHelper: RuleHelper
}

interface GenerateFromProfilePayload {
  calculatedCurveBefore?: LoadCurve
  calculatedCurveAfter?: LoadCurve
}

interface GenerateFromProfileResult {
  calculatedCurveBefore: LoadCurveExt | null
  calculatedCurveAfter: LoadCurveExt | null
}

export const buildLoadModule = ({ post, errorHandler, ruleHelper }: Dependencies) => {
  const getDefaultFields = () => {
    const defaultFields = new ScenarioLoadExt();
    defaultFields.file = new LoadFileExt();
    return defaultFields;
  };

  function getLoadToSend(scenario: ScenarioExt): ScenarioLoad {
    const clone = scenario.clone();
    const fieldStatuses = ruleHelper.getDtoFieldStatuses(clone, []);
    const loadErrors = fieldStatuses.filter(s => !s.isValid() && s.path[0] === 'load');
    if (loadErrors.length) {
      throw new Error('Load has errors');
    }
    ruleHelper.cleanScenario(clone, fieldStatuses);
    const load = clone.load;
    if (!load) {
      throw new Error('Load is not available');
    }
    load.calculatedCurveAfter = null;
    load.calculatedCurveBefore = null;
    return load.toDto();
  }

  @Module({
    namespaced: true
  })
  class LoadModule extends VuexModule {

    private profileHasFile: boolean | undefined = false;

    private loadFileMap: Map<string, File> = new Map();

    @Action({ commit: 'setLoadFileField' })
    public setLoadFile(payload: File | null): { id: string; data: File | null } {
      return { id: this.scenarioId, data: payload };
    }

    @Action
    private async uploadFile(payload: ScenarioLoadExt): Promise<ScenarioLoadExt | null> {
      if (!payload) {
        return null;
      }
      try {
        const body = {
          originalContent: payload.file?.originalContent ?? ''
        };
        const { data } = await post<{values: string[]}>('/load/getValuesFromContent', body, 'getValuesFromContentCancellationToken');
        const updatedScenario = new ScenarioExt({
          ...this.getConfig,
          load: new ScenarioLoadExt({
            ...payload,
            file: new LoadFileExt({
              ...payload.file,
              originalContent: data.values.join('\n')
            })
          })
        });
        this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({ scenario: updatedScenario }));
      } catch (error) {
        errorHandler(error);
      }
      return payload;
    }

    @Action({ rawError: true })
    public async generateFromFile(payload: ScenarioExt): Promise<LoadCurveExt | void> {
      if (!payload) {
        throw new Error('No payload');
      }
      const toSend = getLoadToSend(payload);
      try {
        const { data } = await post<{ calculatedCurve: LoadCurve }>('/load/generateFromFile', toSend, 'getValuesFromFileCancellationToken');
        return LoadCurveExt.fromDto(data.calculatedCurve);
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action({ rawError: true })
    public async generateFromProfile(payload: ScenarioExt): Promise<GenerateFromProfileResult | void> {
      if (!payload) {
        throw new Error('No payload');
      }
      const toSend = getLoadToSend(payload);
      try {
        const { data } = await post<GenerateFromProfilePayload>('/load/generateFromProfile', toSend, 'getValuesFromProfileCancellationToken');
        return {
          calculatedCurveBefore: data.calculatedCurveBefore ? LoadCurveExt.fromDto(data.calculatedCurveBefore) : null,
          calculatedCurveAfter: data.calculatedCurveAfter ? LoadCurveExt.fromDto(data.calculatedCurveAfter) : null
        };
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action({ commit: 'setProfileHasFile' })
    public setHasFile(payload: boolean | null) {
      return payload;
    }

    @Mutation
    private setProfileHasFile(payload: boolean | undefined) {
      this.profileHasFile = payload;
    }

    @Mutation
    private setLoadFileField(payload: { id: string; data: File }) {
      this.loadFileMap.set(payload.id, payload.data);
      this.loadFileMap = new Map(this.loadFileMap);
    }

    get getConfig(): ScenarioExt {
      return new ScenarioExt(this.context.rootGetters['scenario/getFields']);
    }

    get scenarioId(): string {
      return this.getConfig.uuid || '';
    }

    get getProfileHasFile(): boolean | undefined {
      return this.profileHasFile;
    }

    get getFields(): ScenarioLoadExt {
      return this.getConfig.load ? new ScenarioLoadExt(this.getConfig.load) : getDefaultFields();
    }

    get getFile(): File | null {
      return this.loadFileMap.get(this.scenarioId) || null;
    }
  }

  return LoadModule;
};
